import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { menuService, menuTypeService } from '../utils/api';

/**
 * Modal for copying menu data from one menu type to another
 */
const CopyMenuModal = ({ 
  isOpen, 
  onClose, 
  isEditing, 
  currentMonth, 
  currentMenuTypeId,
  school,
  onCopyComplete,
  schoolId
}) => {
  const { t, i18n } = useTranslation();
  const [menuTypes, setMenuTypes] = useState([]);
  const [selectedMenuTypeId, setSelectedMenuTypeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useIntelligentSubstitution, setUseIntelligentSubstitution] = useState(false);

  // Fetch menu types based on condition
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchMenuTypes = async () => {
      setLoading(true);
      setError('');
      try {
        // Get all menus for this month
        const allMenus = await menuService.getMenus(schoolId);
        
        if (!allMenus) {
          setMenuTypes([]);
          setLoading(false);
          return;
        }
        
        // Parse the date
        const date = new Date(currentMonth + "-01");
        const monthNumber = date.getMonth() + 1;
        const year = date.getFullYear();
        
        // Filter menus to those for this month
        const menusForMonth = allMenus.filter(menu => {
          return parseInt(menu.month) === monthNumber && parseInt(menu.year) === year;
        });
        
        // Get all available menu types - pass the current UI language
        console.log(`Fetching menu types with language: ${i18n.language}`);
        const allMenuTypesResponse = await menuTypeService.getMenuTypes(schoolId, i18n.language);
        const allMenuTypes = allMenuTypesResponse || [];
        
        if (isEditing) {
          // When editing, we want menu types that DON'T have a menu for this month
          const menuTypeIdsWithMenusForMonth = new Set(menusForMonth.map(menu => parseInt(menu.menu_type_id)));
          
          // Filter to menu types that don't have a menu for this month
          const availableMenuTypes = allMenuTypes.filter(menuType => 
            !menuTypeIdsWithMenusForMonth.has(menuType.id) && menuType.id !== currentMenuTypeId
          );
          
          setMenuTypes(availableMenuTypes);
        } else {
          // When creating, we want menu types that DO have a menu for this month
          const menuTypeIdsWithMenusForMonth = new Set(menusForMonth.map(menu => parseInt(menu.menu_type_id)));
          
          // Filter to menu types that have a menu for this month
          const availableMenuTypes = allMenuTypes.filter(menuType => 
            menuTypeIdsWithMenusForMonth.has(menuType.id)
          );
          
          setMenuTypes(availableMenuTypes);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching menu types for copy:', err);
        setError(t('admin.errorFetchingMenuTypes'));
        setLoading(false);
      }
    };
    
    fetchMenuTypes();
  }, [isOpen, isEditing, currentMonth, schoolId, currentMenuTypeId, t, i18n.language]);

  const handleCopy = async () => {
    if (!selectedMenuTypeId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Parse month string to get date components
      const date = new Date(currentMonth + "-01");
      const monthNumber = date.getMonth() + 1;
      const year = date.getFullYear();
      
      if (isEditing) {
        // When editing, we're copying FROM the current menu TO a new menu with the selected menu type
        const sourceMenuId = await menuService.checkExistingMenu(
          schoolId,
          currentMonth,
          currentMenuTypeId
        );
        
        if (!sourceMenuId || !sourceMenuId.id) {
          throw new Error('Source menu not found');
        }
        
        // Get the source menu data
        const sourceMenu = await menuService.getMenu(sourceMenuId.id);
        
        // Log the source menu structure to debug
        console.log("Source menu structure:", sourceMenu);
        console.log("Source days structure:", sourceMenu.days[0]);
        
        // Get courses for the target menu type
        const targetMenuTypeCourses = await menuService.getMenuTypeCourses(selectedMenuTypeId);
        const targetCourseIds = new Set(targetMenuTypeCourses.map(course => course.id));
        
        // Create a new menu with the selected menu type, copying items from the source menu
        // that have courses which are also in the target menu type
        const menuData = {
          schoolId: schoolId,
          menuTypeId: parseInt(selectedMenuTypeId),
          month: `${year}-${monthNumber.toString().padStart(2, '0')}-01`, // YYYY-MM-DD format
          useIntelligentSubstitution: useIntelligentSubstitution,
          days: sourceMenu.days.map((day, index) => {
            // Ensure day_number is properly set by trying different property names
            // or fall back to index+1 if all else fails
            const dayNumber = day.day || day.day_number || day.dayNumber || (index + 1);
            
            return {
              day_number: dayNumber,
              special_name_id: day.special_name_id || day.specialNameId,
              source_menu_type_id: currentMenuTypeId,
              items: day.items
                .filter(item => targetCourseIds.has(item.course_id))
                .map(item => ({
                  course_id: item.course_id,
                  dish_id: item.dish_id
                }))
            };
          })
        };
        
        // Create the new menu
        const newMenu = await menuService.createMenu(menuData);
        
        // Call the callback with the new menu ID
        onCopyComplete(newMenu.id);
      } else {
        // When creating, we're copying FROM an existing menu TO the current menu being created
        const sourceMenuId = await menuService.checkExistingMenu(
          schoolId,
          currentMonth,
          selectedMenuTypeId
        );
        
        if (!sourceMenuId || !sourceMenuId.id) {
          throw new Error('Source menu not found');
        }
        
        // Get the source menu data
        const sourceMenu = await menuService.getMenu(sourceMenuId.id);
        
        // Log the source menu structure to debug
        console.log("Source menu structure:", sourceMenu);
        console.log("Source days structure:", sourceMenu.days[0]);
        
        // Get courses for the target menu type (current menu type)
        const targetMenuTypeCourses = await menuService.getMenuTypeCourses(currentMenuTypeId);
        const targetCourseIds = new Set(targetMenuTypeCourses.map(course => course.id));
        
        // Create a new menu with the current menu type, copying items from the source menu
        // that have courses which are also in the target menu type
        const menuData = {
          schoolId: schoolId,
          menuTypeId: parseInt(currentMenuTypeId),
          month: `${year}-${monthNumber.toString().padStart(2, '0')}-01`, // YYYY-MM-DD format
          useIntelligentSubstitution: useIntelligentSubstitution,
          days: sourceMenu.days.map((day, index) => {
            // Ensure day_number is properly set by trying different property names
            // or fall back to index+1 if all else fails
            const dayNumber = day.day || day.day_number || day.dayNumber || (index + 1);
            
            return {
              day_number: dayNumber,
              special_name_id: day.special_name_id || day.specialNameId,
              source_menu_type_id: parseInt(selectedMenuTypeId),
              items: day.items
                .filter(item => targetCourseIds.has(item.course_id))
                .map(item => ({
                  course_id: item.course_id,
                  dish_id: item.dish_id
                }))
            };
          })
        };
        
        // Create the new menu
        const newMenu = await menuService.createMenu(menuData);
        
        // Call the callback with the new menu ID
        onCopyComplete(newMenu.id);
      }
    } catch (err) {
      console.error('Error copying menu:', err);
      setError(t('admin.errorCopyingMenu'));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {t('admin.copyMenu')}
                </h3>
                
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-4">
                    {isEditing 
                      ? t('admin.copyFromCurrentToNew', 'Copy this menu to a new menu type for {{month}}', { month: currentMonth })
                      : t('admin.copyFromExistingToNew', 'Copy from an existing menu for {{month}}', { month: currentMonth })
                    }
                  </p>

                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}

                  {menuTypes.length > 0 ? (
                    <div className="mb-4">
                      <label htmlFor="menuType" className="block text-sm font-medium text-gray-700 mb-1">
                        {isEditing 
                          ? t('admin.selectTargetMenuType', 'Select target menu type')
                          : t('admin.selectSourceMenuType', 'Select source menu type')
                        }
                      </label>
                      <select
                        id="menuType"
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={selectedMenuTypeId}
                        onChange={(e) => setSelectedMenuTypeId(e.target.value)}
                        disabled={loading}
                      >
                        <option value="">{t('admin.selectMenuType', 'Select a menu type...')}</option>
                        {menuTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.translated_name || type.name}
                          </option>
                        ))}
                      </select>
                      
                      {/* Intelligent Substitution Toggle */}
                      <div className="mt-4 flex items-center">
                        <input
                          id="useIntelligentSubstitution"
                          type="checkbox"
                          checked={useIntelligentSubstitution}
                          onChange={(e) => setUseIntelligentSubstitution(e.target.checked)}
                          disabled={loading}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="useIntelligentSubstitution" className="ml-2 block text-sm text-gray-700">
                          {t('admin.useIntelligentSubstitution', 'Attempt historical dish substitution')}
                        </label>
                        <div className="ml-1 relative group">
                          <span className="cursor-help text-gray-400 hover:text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                          <div className="absolute z-10 w-64 p-2 bg-white rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity bottom-6 left-0">
                            <p className="text-xs text-gray-600">
                              {t('admin.intelligentSubstitutionTooltip', 'Uses historical data to intelligently match dishes between menu types based on past usage patterns.')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
                      {isEditing 
                        ? t('admin.noAvailableMenuTypes', 'All menu types already have a menu for this month')
                        : t('admin.noExistingMenus', 'No menu types have a menu for this month yet')
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {menuTypes.length > 0 && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleCopy}
                disabled={loading || !selectedMenuTypeId}
              >
                {loading ? t('general.loading') : t('general.accept')}
              </button>
            )}
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
              disabled={loading}
            >
              {t('general.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyMenuModal; 